import { IsBoolean, IsDate, IsIn, IsOptional, IsString, IsUUID } from 'class-validator';
import {
  INGREDIENT_ORGANIC_STATUSES,
  type IngredientOrganicStatus,
} from './ingredient-organic-status.enum';
import { NUTRITION_SCOPES, type NutritionScope } from './nutrition-scope.enum';

/**
 * Effective organic declaration for one ingredient as resolved for the
 * calling tenant, in the same precedence/scope vocabulary as
 * `EffectiveNutritionResponseDto` (`NUTRITION_SCOPES`): `CUSTOM` for a
 * tenant-owned ingredient, `GLOBAL_OVERRIDE` for a global ingredient with
 * this tenant's own declaration attached, `GLOBAL` for a global ingredient no
 * tenant declaration exists for yet.
 *
 * Unlike nutrition/physical factors, there is no "fallback to the shared
 * global value" here: a global `Ingredient` row never carries an organic
 * declaration of its own (see the schema comment on `Ingredient.organicStatus`),
 * so `GLOBAL` always means `organicStatus: null` — nobody has declared it.
 *
 * `declaredById`/`declaredByName` are never another tenant's identity: this
 * response only ever reflects the calling tenant's own declaration.
 */
export class IngredientOrganicDeclarationResponseDto {
  @IsUUID()
  ingredientId!: string;

  @IsBoolean()
  isGlobalCatalogEntry!: boolean;

  @IsIn(NUTRITION_SCOPES)
  scope!: NutritionScope;

  @IsOptional()
  @IsIn(INGREDIENT_ORGANIC_STATUSES)
  organicStatus?: IngredientOrganicStatus | null;

  @IsOptional()
  @IsUUID()
  organicProofDocumentId?: string | null;

  @IsOptional()
  @IsUUID()
  declaredById?: string | null;

  @IsOptional()
  @IsString()
  declaredByName?: string | null;

  @IsOptional()
  @IsDate()
  declaredAt?: Date | null;
}
